// src/utils/geoUtils.js — Geolocalización con fallback a IP

/**
 * requestGeolocation
 * Solicita permiso de GPS al usuario.
 * Si rechaza o no está disponible, devuelve null.
 *
 * @returns {Promise<{ lat: number, lng: number, countryCode: string|null }|null>}
 */
export async function requestGeolocation() {
  if (!('geolocation' in navigator)) return null;

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        // Intentar reverse geocoding para obtener el país
        const countryCode = await reverseGeocode(lat, lng);
        resolve({ lat, lng, countryCode });
      },
      () => resolve(null),    // Usuario denegó o error
      { timeout: 8000, maximumAge: 300_000 }
    );
  });
}

/**
 * reverseGeocode
 * Obtiene el código de país (ISO 3166-1 alpha-2) a partir de coordenadas.
 * Usa ip-api.com como servicio gratuito (sin key requerida).
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<string|null>} - e.g. 'MX', 'US', null
 */
async function reverseGeocode(lat, lng) {
  try {
    const res  = await fetch(`https://ip-api.com/json/?fields=countryCode`, { signal: AbortSignal.timeout(4000) });
    const data = await res.json();
    return data.countryCode || null;
  } catch {
    return null;
  }
}

/**
 * getCountryFromIP
 * Fallback: obtiene el país del usuario por IP cuando no hay GPS.
 * Se usa para determinar la firma del PDF.
 *
 * @returns {Promise<string|null>} - country code
 */
export async function getCountryFromIP() {
  try {
    const res  = await fetch('https://ip-api.com/json/?fields=countryCode', { signal: AbortSignal.timeout(4000) });
    const data = await res.json();
    return data.countryCode || null;
  } catch {
    return null;
  }
}

/**
 * formatCoordinates
 * Formatea coordenadas para mostrar en PDF y UI.
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {string} - e.g. "25.6866°N, -100.3161°W"
 */
export function formatCoordinates(lat, lng) {
  const latDir = lat >= 0 ? 'N' : 'S';
  const lngDir = lng >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(4)}°${latDir}, ${Math.abs(lng).toFixed(4)}°${lngDir}`;
}
